import {useUniversalFetch} from '#imports';
import type {$Fetch} from 'nitropack';
import type {UseAbortableOptions} from './useAbortable';
import {useAbortable} from './useAbortable';

/**
 * Transform any OpenAPI request into a abortable request
 *
 * @param options - Optional parameter to customize the behavior
 * @param options.automatedAbort - If true the previous request will be automatically aborted if a new one is started, defaults to true.
 *
 * @usage
 *
 * ```ts*
 * export const userStore = defineStore('user', () => {
 *
 *     const {execute} = useAbortableUniversalFetch();
 *
 *     async function searchUser(query: string) {
 *         // This function will be aborted if a new search is triggered, and result will be undefined
 *         const result = await execute('/users', {method: 'GET'});
 *         if (result) {
 *             users.value = result
 *         }
 *     }
 * })
 * ```
 */
export function useAbortableUniversalFetch(options: Partial<UseAbortableOptions> = {}) {
    const {execute} = useAbortable(options);

    async function fetchAndMaybeAbort<ResponseType>(
        url: string,
        options: Parameters<$Fetch>[1] = {method: 'GET'},
    ): Promise<ResponseType | undefined> {
        return await execute((signal) => useUniversalFetch<ResponseType>(url, {signal, ...options}));
    }

    return {
        execute: fetchAndMaybeAbort,
    };
}
